import {Property} from '@mintdata/mint-kit/api/decorators/Property';
import {BorderFill} from '@mintdata/mint-kit/api/models/BorderFill';
import {BorderRadius} from '@mintdata/mint-kit/api/models/BorderRadius';
import {BorderStyle} from '@mintdata/mint-kit/api/models/BorderStyle';
import {Color} from '@mintdata/mint-kit/api/models/Color';
import {ColorWithVisibility} from '@mintdata/mint-kit/api/models/ColorWithVisibility';
import {PositiveUnits} from '@mintdata/mint-kit/api/models/PositiveUnits';
import {PropertyCategory} from '@mintdata/mint-kit/api/properties/PropertyCategory';
import {ABoolean} from '@mintdata/mint-kit/api/values/ABoolean';
import {AStringEnumConfig} from '@mintdata/mint-kit/impl/properties/typeConfigs/AEnumConfig';
import {BeanConfig} from '@mintdata/mint-kit/impl/properties/typeConfigs/BeanConfig';
import {BorderRadiusConfig} from '@mintdata/mint-kit/impl/properties/typeConfigs/BorderRadiusConfig';
import {PositiveUnitsConfig} from '@mintdata/mint-kit/impl/properties/typeConfigs/PositiveUnitsConfig';

export const DEFAULT_INPUT_BORDER_COLOR:string = '#cdced0';

export class Border
{
    @Property<ColorWithVisibility>({
        title: 'Border Color',
        tip: 'Border Color',
        isRequired: true,
        typeConfig: BeanConfig,
        typeCtor: ColorWithVisibility
    })
    borderColor:ColorWithVisibility;

    @Property<PositiveUnits>({
        title: 'Size',
        tip: 'Size',
        isRequired: true,
        typeConfig: PositiveUnitsConfig,
        typeCtor: PositiveUnits
    })
    borderThickness:PositiveUnits;

    @Property<BorderStyle>({
        title: 'Border Style',
        tip: 'Border Style',
        isRequired: true,
        typeConfig: AStringEnumConfig,
        typeCtor: BorderStyle
    })
    borderStyle:BorderStyle;

    @Property<BorderRadius>({
        title: 'Border Radius',
        tip: 'Border Radius',
        category: PropertyCategory.Layout,
        isRequired: false,
        typeConfig: BorderRadiusConfig,
        typeCtor: BorderRadius
    })
    borderRadius:BorderRadius = BorderRadius.fromEqualPixels(0);

    @Property<BorderFill>({
        title: 'Border Fill',
        tip: 'Border Fill',
        isRequired: true,
        typeConfig: AStringEnumConfig,
        typeCtor: BorderFill
    })
    borderFill:BorderFill;

    public constructor(borderColor:ColorWithVisibility,
                       borderThickness:PositiveUnits,
                       borderStyle:BorderStyle,
                       borderRadius:BorderRadius,
                       borderFill:BorderFill)
    {
        this.borderColor = borderColor;
        this.borderThickness = borderThickness;
        this.borderStyle = borderStyle;
        this.borderRadius = borderRadius;
        this.borderFill = borderFill;
    }

    public toCSS():React.CSSProperties
    {
        const {
            borderColor,
            borderThickness,
            borderStyle,
            borderFill,
            borderRadius
        } = this;

        const borderStyleCss:string = borderStyle.toString();
        const borderWidthCss = borderColor.visibility.value ? borderThickness.toString() : 0;
        const borderColorCss:string = borderColor.toString();
        const borderStyles:string = `${borderStyleCss} ${borderWidthCss} ${borderColorCss}`;

        const borderStylesObject = BorderFill.isAll(borderFill)
            ? {
                borderTop: borderStyles,
                borderLeft: borderStyles,
                borderBottom: borderStyles,
                borderRight: borderStyles
            }
            : {
                borderTop: !BorderFill.isTop(borderFill) ? 'none' : borderStyles,
                borderLeft: !BorderFill.isLeft(borderFill) ? 'none' : borderStyles,
                borderBottom: !BorderFill.isBottom(borderFill) ? 'none' : borderStyles,
                borderRight: !BorderFill.isRight(borderFill) ? 'none' : borderStyles
            };

        return {
            ...borderStylesObject,
            borderTopLeftRadius: borderRadius && borderRadius.topLeft && borderRadius.topLeft.toString(),
            borderTopRightRadius: borderRadius && borderRadius.topRight && borderRadius.topRight.toString(),
            borderBottomRightRadius: borderRadius && borderRadius.bottomRight && borderRadius.bottomRight.toString(),
            borderBottomLeftRadius: borderRadius && borderRadius.bottomLeft && borderRadius.bottomLeft.toString()
        };
    };

    public clone():Border
    {
        return new Border(
            this.borderColor,
            this.borderThickness,
            this.borderStyle,
            this.borderRadius,
            this.borderFill
        );
    }

    public static default():Border
    {
        return new Border(
            ColorWithVisibility.blackInvisible(),
            PositiveUnits.fromPixels(1),
            BorderStyle.solid(),
            BorderRadius.fromEqualPixels(0),
            BorderFill.all()
        );
    }

    public static defaultForInput():Border
    {
        return new Border(
            new ColorWithVisibility(Color.fromHexString(DEFAULT_INPUT_BORDER_COLOR), ABoolean.true()),
            PositiveUnits.fromPixels(1),
            BorderStyle.solid(),
            BorderRadius.fromEqualPixels(3),
            BorderFill.all()
        );
    }
}
